import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";

/**
 * A brand's guests — its clients, signed in with their own accounts — and the
 * guest invites still waiting to be accepted. They are who a month and a
 * strategy are sent to. Listing is for editors; inviting and removing is for
 * org Owners (the backend enforces both).
 */
export interface BrandGuest {
  userId: string;
  name: string | null;
  email: string;
  grantedAt: string;
  grantedBy: string;
  profilePicture?: string | null;
  lastSeenAt?: string | null;
}

export interface BrandGuestInvite {
  id: string;
  email: string;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
}

export interface BrandGuests {
  guests: BrandGuest[];
  invites: BrandGuestInvite[];
}

interface InviteGuestResult {
  message: string;
  granted: boolean;
  emailSent: boolean;
}

export const brandGuestsKey = (brandId: string | null) => ["brandGuests", brandId];

/** Prefix of an invite row shown before the server answers. */
const OPTIMISTIC_INVITE = "optimistic:";

export function isOptimisticInvite(invite: BrandGuestInvite): boolean {
  return invite.id.startsWith(OPTIMISTIC_INVITE);
}

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

export function useBrandGuests(brandId: string | null, enabled = true) {
  return useQuery({
    queryKey: brandGuestsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<BrandGuests>(`/brand/${brandId}/guests`);
      return res.data;
    },
    enabled: !!brandId && enabled,
  });
}

/** Who a month or a strategy can be sent to follows the guest list. */
function invalidateRecipients(qc: ReturnType<typeof useQueryClient>, brandId: string | null) {
  void qc.invalidateQueries({ queryKey: brandGuestsKey(brandId) });
  void qc.invalidateQueries({ queryKey: ["calendarNotifyState"] });
  void qc.invalidateQueries({ queryKey: ["strategyShares", brandId] });
}

/**
 * Invite someone as a guest of this brand. An existing guest of the org is
 * granted the brand straight away; anyone else gets a guest invite email.
 * Shows as a pending invite until the server answers.
 */
export function useInviteBrandGuest(brandId: string | null) {
  const qc = useQueryClient();
  const key = brandGuestsKey(brandId);
  return useMutation({
    mutationFn: async (email: string) => {
      const res = await axiosInstance.post<InviteGuestResult>(`/brand/${brandId}/guests`, { email });
      return res.data;
    },
    onMutate: async (email) => {
      await qc.cancelQueries({ queryKey: key });
      const tempId = `${OPTIMISTIC_INVITE}${email}`;
      qc.setQueryData<BrandGuests>(key, (prev) =>
        prev
          ? {
              ...prev,
              invites: [
                ...prev.invites.filter((i) => i.email.toLowerCase() !== email.toLowerCase()),
                {
                  id: tempId,
                  email,
                  invitedBy: "",
                  createdAt: new Date().toISOString(),
                  expiresAt: new Date().toISOString(),
                },
              ],
            }
          : prev,
      );
      return { tempId };
    },
    onError: (err, _email, ctx) => {
      qc.setQueryData<BrandGuests>(key, (prev) =>
        prev && ctx ? { ...prev, invites: prev.invites.filter((i) => i.id !== ctx.tempId) } : prev,
      );
      queue.add({
        title: "Could not invite that guest",
        description: errorMessage(err, "Please check the email and try again."),
        variant: "error",
      });
    },
    onSuccess: (result) => {
      queue.add({
        title: result.granted ? "Guest added" : "Guest invited",
        description: result.emailSent
          ? result.message
          : `${result.message} The email could not be sent; they can still sign in to see it.`,
        variant: result.emailSent ? "success" : "warning",
      });
    },
    onSettled: () => invalidateRecipients(qc, brandId),
  });
}

/**
 * Take this brand away from a guest. A guest left with no brands in the org
 * loses the membership too.
 */
export function useRemoveBrandGuest(brandId: string | null) {
  const qc = useQueryClient();
  const key = brandGuestsKey(brandId);
  return useMutation({
    mutationFn: async (userId: string) => {
      await axiosInstance.delete(`/brand/${brandId}/guests/${userId}`);
    },
    onMutate: async (userId) => {
      await qc.cancelQueries({ queryKey: key });
      const removed = qc.getQueryData<BrandGuests>(key)?.guests.find((g) => g.userId === userId);
      qc.setQueryData<BrandGuests>(key, (prev) =>
        prev ? { ...prev, guests: prev.guests.filter((g) => g.userId !== userId) } : prev,
      );
      return { removed };
    },
    onError: (err, _userId, ctx) => {
      const removed = ctx?.removed;
      if (removed) {
        qc.setQueryData<BrandGuests>(key, (prev) =>
          prev && !prev.guests.some((g) => g.userId === removed.userId)
            ? { ...prev, guests: [...prev.guests, removed] }
            : prev,
        );
      }
      queue.add({
        title: "Could not remove that guest",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => invalidateRecipients(qc, brandId),
  });
}

/** Withdraw this brand from a guest invite that hasn't been accepted yet. */
export function useCancelBrandGuestInvite(brandId: string | null) {
  const qc = useQueryClient();
  const key = brandGuestsKey(brandId);
  return useMutation({
    mutationFn: async (inviteId: string) => {
      await axiosInstance.delete(`/brand/${brandId}/guests/invites/${inviteId}`);
    },
    onMutate: async (inviteId) => {
      await qc.cancelQueries({ queryKey: key });
      const removed = qc.getQueryData<BrandGuests>(key)?.invites.find((i) => i.id === inviteId);
      qc.setQueryData<BrandGuests>(key, (prev) =>
        prev ? { ...prev, invites: prev.invites.filter((i) => i.id !== inviteId) } : prev,
      );
      return { removed };
    },
    onError: (err, _inviteId, ctx) => {
      const removed = ctx?.removed;
      if (removed) {
        qc.setQueryData<BrandGuests>(key, (prev) =>
          prev && !prev.invites.some((i) => i.id === removed.id)
            ? { ...prev, invites: [...prev.invites, removed] }
            : prev,
        );
      }
      queue.add({
        title: "Could not cancel that invite",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => invalidateRecipients(qc, brandId),
  });
}
